import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Shirt, Home, Baby, LayoutGrid, PackageOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t, language } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
    };
    fetchTestimonials();
  }, []);

  const services = [
    {
      icon: Shirt,
      title: t('services.closet.title'),
      description: t('services.closet.description'),
      link: '/services#closet',
    },
    {
      icon: Home,
      title: t('services.decluttering.title'),
      description: t('services.decluttering.description'),
      link: '/services#decluttering',
    },
    {
      icon: Baby,
      title: t('services.newborn.title'),
      description: t('services.newborn.description'),
      link: '/services#newborn',
    },
    {
      icon: LayoutGrid,
      title: t('services.rooms.title'),
      description: t('services.rooms.description'),
      link: '/services#rooms',
    },
    {
      icon: PackageOpen,
      title: t('services.unpacking.title'),
      description: t('services.unpacking.description'),
      link: '/services#unpacking',
    },
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/7qaarhdv_IMG_4794.jpeg"
            alt="Serene organized living space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C3333]/70 via-[#2C3333]/40 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
          <div className="max-w-2xl">
            <span className="inline-block font-accent text-lg md:text-xl text-[#8DA399] mb-6 animate-fade-in-up">
              {t('hero.subtitle')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight animate-fade-in-up animation-delay-100">
              {t('hero.title')}
              <br />
              <span className="text-[#8DA399] italic">{t('hero.titleAccent')}</span>
            </h1>
            <p className="mt-8 font-sans text-base md:text-lg text-white/80 leading-relaxed max-w-xl animate-fade-in-up animation-delay-200">
              {t('hero.description')}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/services">
                <Button
                  data-testid="hero-cta-primary"
                  className="bg-[#8DA399] text-white hover:bg-[#7A8F85] rounded-full px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#2C3333] rounded-full px-8 py-6 text-lg transition-all duration-300"
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section
        data-testid="services-preview-section"
        className="py-24 md:py-32 bg-[#F2F0E9]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="font-accent text-lg text-[#8DA399]">
              {t('services.subtitle')}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#2C3333] mt-4">
              {t('services.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                data-testid={`service-card-${index}`}
                className="group bg-white p-8 md:p-12 border border-[#E5E5E0] hover:border-[#8DA399]/50 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start space-x-6">
                  <div className="p-4 bg-[#F2F0E9] group-hover:bg-[#8DA399]/10 transition-colors rounded-sm">
                    <service.icon className="w-8 h-8 text-[#8DA399]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl md:text-2xl text-[#2C3333] group-hover:text-[#8DA399] transition-colors">
                      {service.title}
                    </h3>
                    <p className="mt-4 font-sans text-[#5C5C5C] leading-relaxed">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center mt-6 text-[#8DA399] font-medium group-hover:translate-x-2 transition-transform">
                      {t('services.learnMore')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button
                data-testid="services-cta"
                className="bg-[#8DA399] text-white hover:bg-[#7A8F85] rounded-full px-10 py-6 text-lg"
              >
                {t('services.requestQuote')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section
          data-testid="testimonials-section"
          className="py-24 md:py-32 bg-[#FAFAF9]"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-16">
              <span className="font-accent text-lg text-[#8DA399]">
                {t('testimonials.subtitle')}
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#2C3333] mt-4">
                {t('testimonials.title')}
              </h2>
            </div>

            <div className="relative max-w-4xl mx-auto">
              <div
                data-testid="testimonial-card"
                className="bg-[#F2F0E9] p-10 md:p-16 text-center"
              >
                <blockquote className="font-serif text-xl md:text-2xl text-[#2C3333] italic leading-relaxed">
                  {language === 'en'
                    ? testimonials[currentTestimonial]?.text_en
                    : testimonials[currentTestimonial]?.text_es}
                </blockquote>
                <div className="mt-8">
                  <p className="font-sans font-medium text-[#2C3333]">
                    {testimonials[currentTestimonial]?.name}
                  </p>
                  <p className="font-sans text-sm text-[#5C5C5C]">
                    {testimonials[currentTestimonial]?.location}
                  </p>
                </div>
              </div>

              {/* Navigation Arrows */}
              <div className="flex justify-center items-center mt-8 space-x-4">
                <button
                  onClick={prevTestimonial}
                  data-testid="testimonial-prev"
                  className="p-3 border border-[#E5E5E0] hover:border-[#8DA399] hover:bg-[#8DA399] hover:text-white transition-all rounded-full"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      data-testid={`testimonial-dot-${index}`}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentTestimonial
                          ? 'bg-[#8DA399] w-6'
                          : 'bg-[#E5E5E0]'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={nextTestimonial}
                  data-testid="testimonial-next"
                  className="p-3 border border-[#E5E5E0] hover:border-[#8DA399] hover:bg-[#8DA399] hover:text-white transition-all rounded-full"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section
        data-testid="cta-section"
        className="py-24 md:py-32 bg-[#8DA399]"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            {language === 'en'
              ? 'Ready to Transform Your Space?'
              : '¿Listo para Transformar Tu Espacio?'}
          </h2>
          <p className="mt-6 font-sans text-lg text-white/80 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Let\'s create a home that brings you peace and joy every single day.'
              : 'Creemos un hogar que te traiga paz y alegría cada día.'}
          </p>
          <div className="mt-10">
            <Link to="/contact">
              <Button
                data-testid="cta-contact-button"
                className="bg-white text-[#8DA399] hover:bg-[#F2F0E9] rounded-full px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
              >
                {t('hero.ctaSecondary')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
